import { Box } from '@src/atoms/Box';
import { Text } from '@src/atoms/Text';
import { Popover } from '@src/atoms/Popover';
import { useClickOutside } from '@src/hooks/useClickOutside';
import { useOpen } from '@src/hooks/useOpen';
import { IComponentChildrenProps } from '@src/models/props';
import { SwipeDialogContext } from '@src/molecules/SwipeDialog';
import React, { FC, useState, useContext, useCallback, useEffect } from 'react';
import styles from './DatePickerCustomTimeInput.module.scss';
import { getHourList, getMinutesList } from './utils';
import { Icon } from '@src/atoms/Icon';

type TBtnKind = 'hour' | 'minute';

interface DatePickerCustomTimeInputBtnProps extends IComponentChildrenProps {
  id: string;
  hours: Array<string | number>;
  minutes: Array<string | number>;
  onClick: (kind: TBtnKind, v: string | Date) => void;
}

const DatePickerCustomTimeInputBtn: FC<DatePickerCustomTimeInputBtnProps> = ({
  id,
  children,
  hours,
  minutes,
  onClick,
}) => {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const { open, onToggle, onClose } = useOpen(false);
  const { swipeController } = useContext(SwipeDialogContext);

  useClickOutside(ref, onClose);

  const handleClick = useCallback(
    (kind, v) => () => {
      onClick(kind, v);
    },
    [onClick],
  );

  /**
   * В мобилке при открытии списка нужно выключать свайпер,
   * чтобы дать возможность пользователю свайпать скролл у этого контейнера
   **/
  useEffect(() => {
    if (open) {
      swipeController?.disable();
    } else {
      swipeController?.enable();
    }
  }, [open, swipeController]);

  return (
    <>
      {/** Нужно не button https://centrateam.atlassian.net/browse/CA-3733 */}
      <label ref={setRef} data-id={id} onClick={onToggle} className={styles.btn}>
        {children}
      </label>

      {ref && (
        <Popover open={open} anchorEl={ref} container={ref}>
          <Box paddingTop={8} paddingBottom={8} className={styles.listWrapper}>
            <Box bgColor="basicWhite" className={styles.listContainer} column>
              {hours.map((value) => (
                <Box
                  key={value}
                  alignItems="center"
                  justifyContent="center"
                  paddingTop={8}
                  paddingBottom={8}
                  paddingLeft={24}
                  paddingRight={24}
                  onClick={handleClick('hour', value)}
                  className={styles.listContainerItem}
                >
                  {value}
                </Box>
              ))}
            </Box>
            <Box bgColor="basicWhite" className={styles.listContainer} column>
              {minutes.map((value) => (
                <Box
                  key={value}
                  alignItems="center"
                  justifyContent="center"
                  paddingTop={8}
                  paddingBottom={8}
                  paddingLeft={24}
                  paddingRight={24}
                  onClick={handleClick('minute', value)}
                  className={styles.listContainerItem}
                >
                  {value}
                </Box>
              ))}
            </Box>
          </Box>
        </Popover>
      )}
    </>
  );
};

interface DatePickerCustomTimeInputProps {
  date: Date;
  value: string;
  onChange: (d: string | Date, e?: React.ChangeEvent<HTMLInputElement>) => void;
}

export const DatePickerCustomTimeInput: FC<DatePickerCustomTimeInputProps> = ({ date, value, onChange }) => {
  const handleChange = (kind: TBtnKind, v: string | Date) => {
    const [h, m] = value.split(':');
    const newValue = kind === 'hour' ? `${v}:${m}` : `${h}:${v}`;

    onChange?.(newValue);
  };

  return (
    <Box alignItems="center" justifyContent="center" gap={4} fullWidth>
      <DatePickerCustomTimeInputBtn
        onClick={handleChange}
        id="DatePickerTimeBtn"
        hours={getHourList()}
        minutes={getMinutesList()}
      >
        <Box gap={28} className={styles.timeWrapper}>
          <Text id="DatePickerTime" size="secondary">
            {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Text>
          <Icon name={'clock'} size={'md-light'} />
        </Box>
      </DatePickerCustomTimeInputBtn>
    </Box>
  );
};
